import * as React from 'react';
import { Link } from 'react-router-dom';
import { Building, CalendarDays, CalendarPlus, MapPin } from 'lucide-react';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';

import type { DashboardMeeting } from '../types';
import { normalizeMeetingStatus } from '../types';

interface AdminUpcomingMeetingsProps {
  meetings: DashboardMeeting[];
}

function formatTime(time: string): string {
  if (!time) return '';
  const [hStr, mStr] = time.split(':');
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${mStr ?? '00'} ${ampm}`;
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Upcoming meetings list card content for the Operations Command Center.
 */
export function AdminUpcomingMeetings({ meetings }: AdminUpcomingMeetingsProps) {
  if (meetings.length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        <p className="tw-text-sm tw-text-muted-foreground">
          No upcoming meetings.
        </p>
        <Button asChild size="sm" className="tw-w-full">
          <Link to="/meetings/create">
            <CalendarPlus className="tw-mr-2 tw-h-4 tw-w-4" />
            Schedule meeting
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-divide-y tw-divide-border tw-p-0">
        {meetings.map((meeting) => {
          const status = normalizeMeetingStatus(meeting.meetingStatus);
          return (
            <li key={meeting.id} className="first:tw-pt-0 last:tw-pb-0">
              <Link
                to={`/meetings/view/${meeting.id}`}
                className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-px-1.5 tw-py-2 tw-no-underline hover:tw-bg-muted/50"
              >
                <span
                  className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10 tw-text-primary"
                  aria-hidden="true"
                >
                  <CalendarDays className="tw-h-4 tw-w-4" />
                </span>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <p className="tw-m-0 tw-truncate tw-text-[13px] tw-font-semibold tw-text-foreground">
                      {meeting.meetingTitle || 'Meeting'}
                    </p>
                    {status === 4 ? <Badge variant="warning">Rescheduled</Badge> : null}
                  </div>
                  <p className="tw-m-0 tw-mt-0.5 tw-flex tw-items-center tw-gap-2 tw-text-[11.5px] tw-text-muted-foreground">
                    <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
                      <Building className="tw-h-3 tw-w-3 tw-shrink-0" />
                      <span className="tw-truncate">{meeting.clientName || 'N/A'}</span>
                    </span>
                    {meeting.meetingLocation ? (
                      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
                        <MapPin className="tw-h-3 tw-w-3 tw-shrink-0" />
                        <span className="tw-truncate">{meeting.meetingLocation}</span>
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="tw-shrink-0 tw-text-right">
                  <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-foreground">
                    {formatTime(meeting.meetingStartTime)}
                  </p>
                  <p className="tw-m-0 tw-text-[11.5px] tw-text-muted-foreground">
                    {formatDate(meeting.meetingDate)}
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
      <Button asChild size="sm" className="tw-mt-2 tw-w-full">
        <Link to="/meetings/create">
          <CalendarPlus className="tw-mr-2 tw-h-4 tw-w-4" />
          Schedule meeting
        </Link>
      </Button>
    </div>
  );
}
